import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { CardGrid } from '@iheartjane/menu';
import { Box, Flex, Link, useMobileMediaQuery } from '@iheartjane/reefer';
import type { Breadcrumb } from '@iheartjane/types';
import { useMemo } from 'react';

import { useSearchContext } from '@jane/search/hooks';
import type { AlgoliaProduct } from '@jane/search/types';
import type { ProductCardWithTrackingProps } from '@jane/shared-ecomm/components';
import { MenuProductGrid } from '@jane/shared-ecomm/components';
import type { UseMyHighResponse } from '@jane/shared-ecomm/hooks';
import { useMenu } from '@jane/shared-ecomm/providers';
import { scrollToTop } from '@jane/shared-ecomm/util';
import type { MenuProduct } from '@jane/shared/types';
import { getAppliedWeightFilter, getGridPosition } from '@jane/shared/util';

import { MenuCarousel } from '../../MenuPages/MenuFeatured/MenuCarousel';
import { MenuEmptyResults } from '../MenuEmptyResults/MenuEmptyResults';
import { MenuProductCard } from '../MenuProductCards/MenuProductCard/MenuProductCard';

export interface MenuProductsGridProps
  extends
    Pick<
      UseMyHighResponse<AlgoliaProduct>,
      | 'fetchNextPage'
      | 'hasNextPage'
      | 'indexName'
      | 'isLoading'
      | 'products'
      | 'numHits'
      | 'numColumns'
    >,
    Pick<UseMyHighResponse<AlgoliaProduct>, 'dynamicPlacement'> {
  additionalProductFilter?: (
    products: SmartSortProduct<AlgoliaProduct>[]
  ) => SmartSortProduct<AlgoliaProduct>[];
  ariaLabel?: string;
  breadcrumbs?: Breadcrumb[];
  maxWeightOption?: number | null;
  noPadding?: boolean;
  pageTitle: string;
  showOnlyWeights?: ProductCardWithTrackingProps['showOnlyWeights'];
  weightFilter?: (
    weight: string,
    product: MenuProduct | AlgoliaProduct
  ) => boolean;
  withoutDrawerOnAddToCart?: boolean;
}

export const MenuProductsGrid = ({
  additionalProductFilter,
  ariaLabel,
  breadcrumbs,
  dynamicPlacement,
  fetchNextPage,
  hasNextPage,
  indexName,
  isLoading,
  maxWeightOption,
  noPadding = false,
  numColumns,
  numHits,
  pageTitle,
  products,
  showOnlyWeights,
  weightFilter,
  withoutDrawerOnAddToCart,
}: MenuProductsGridProps) => {
  const midGridAdRow = useFlag(FLAGS.c3MidGridAdRow);
  const isMobile = useMobileMediaQuery({});
  const { searchState } = useSearchContext();
  const { listView } = useMenu();

  const productsToRender = useMemo(() => {
    return additionalProductFilter
      ? additionalProductFilter(products)
      : products;
  }, [products, products.length, additionalProductFilter]);

  return (
    <Box id="results" aria-label={ariaLabel}>
      <MenuProductGrid
        listView={listView}
        hasMore={hasNextPage}
        onViewMoreClick={fetchNextPage}
        hideTotal={!!additionalProductFilter}
        isLoading={isLoading}
        isLoadingMore={isLoading}
        name={pageTitle}
        totalRenderedProducts={productsToRender.length}
        totalResults={numHits}
        px={noPadding ? 0 : isMobile ? 24 : 64}
      >
        {(() => {
          const placementIndex = (numColumns ?? 4) * 2;
          const showPlacement = midGridAdRow && dynamicPlacement;
          const placementElement = showPlacement && (
            <CardGrid.Placement
              key="mid-grid-placement"
              background="primary"
              py={24}
            >
              <MenuCarousel
                index={1}
                placement={dynamicPlacement}
                disableWeightFilter
              />
            </CardGrid.Placement>
          );

          const cards =
            productsToRender.length > 0 &&
            productsToRender.map((product, index) => {
              const { row, column } = getGridPosition(
                index,
                numColumns ?? undefined
              );

              const productKey = `${product?.isSponsored ? 'inline-ad-' : 'mp-'}${
                product.objectId
              }`;

              return (
                <MenuProductCard
                  key={productKey}
                  appliedWeightFilter={getAppliedWeightFilter(searchState)}
                  algoliaIndexName={indexName}
                  breadcrumbs={breadcrumbs}
                  columnPosition={column}
                  maxWeightOption={maxWeightOption}
                  menuRowName={pageTitle}
                  product={product}
                  productLocation={pageTitle}
                  rowPosition={row}
                  showOnlyWeights={showOnlyWeights}
                  {...(weightFilter && { weightFilter })}
                  withoutDrawerOnAddToCart={withoutDrawerOnAddToCart}
                />
              );
            });

          if (!cards) return null;
          if (!placementElement) return cards;

          {
            /* a11y feature: ensures the carousel is correctly positioned in the dom */
          }
          const insertAt = Math.min(placementIndex, cards.length);
          return [
            ...cards.slice(0, insertAt),
            placementElement,
            ...cards.slice(insertAt),
          ];
        })()}
      </MenuProductGrid>
      {!isLoading && products.length === 0 ? (
        <MenuEmptyResults />
      ) : (
        <Flex
          flexDirection="column"
          alignItems="center"
          pt={24}
          px={isMobile ? 24 : 48}
        >
          <Link onClick={scrollToTop}>Back to top</Link>
        </Flex>
      )}
    </Box>
  );
};
